"use client";

/**
 * Route-level Error Page
 *
 * Next.js automatically wraps every route in a React ErrorBoundary.
 * This file replaces the default white "Application error" screen
 * with a branded recovery UI.
 *
 * Triggers: Any uncaught error in the React render tree for the "/" route.
 * Recovery: "Reload Page" button does a full page reload to reset all state.
 *
 * This does NOT need to be called manually — Next.js activates it
 * automatically when an error propagates up to the route boundary.
 */

import { useEffect } from "react";

export default function Error({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    console.error("[ErrorPage] Caught unhandled error:", error);
  }, [error]);

  return (
    <div className="flex min-h-[100dvh] items-center justify-center bg-background px-4">
      <div className="text-center max-w-sm">
        {/* Warning icon */}
        <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
          <svg className="h-8 w-8 text-primary" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z" />
          </svg>
        </div>

        <h1 className="font-display text-xl text-foreground mb-2">
          Something went wrong
        </h1>
        <p className="text-sm text-muted-foreground mb-8">
          Your session has ended. Please reload to continue.
        </p>

        <button
          onClick={() => { try { reset(); } catch { window.location.reload(); } }}
          className="inline-flex items-center justify-center rounded-xl bg-primary px-6 py-3 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors w-full"
        >
          Reload Page
        </button>
      </div>
    </div>
  );
}
